import React, { useEffect, useState } from 'react';
import { courseTaskService } from '../../services/courseTaskService';
import { courseService } from '../../services/courseService';
import { chapterService, Chapter } from '../../services/chapterService';
import { videoService } from '../../services/videoService';
import { Task, Course, Video, TaskFormItem } from '../../types';
import { useToast } from '../../components/Toast';

type FormItemDraft = TaskFormItem;

type MultipleChoiceDraft = {
  id: string;
  text: string;
  exp: number;
  popup_message: string;
  popup_voice_url?: string; // Existing voice URL from backend
};

const createEmptyFormItem = (): FormItemDraft => ({
  id: typeof crypto !== 'undefined' && crypto.randomUUID ? crypto.randomUUID() : `${Date.now()}-${Math.random()}`,
  question: '',
  description: '',
  placeholder: '',
  require_answer: true,
  require_check: true,
});

const createEmptyChoice = (): MultipleChoiceDraft => ({
  id: typeof crypto !== 'undefined' && crypto.randomUUID ? crypto.randomUUID() : `${Date.now()}-${Math.random()}`,
  text: '',
  exp: 0,
  popup_message: '',
});

type NewCourseTaskForm = {
  title: string;
  description: string;
  points: number;
  task_type: Task['task_type'];
  is_active: boolean;
  requirementCount: number;
  video_id: number | '';
  chapter_id: number | '';
  order: number;
  form_items: FormItemDraft[];
  allow_file_upload: boolean;
  require_all_done: boolean;
  // Multiple choice fields
  multiple_choice_intro_popup: string;
  multiple_choice_question: string;
  multiple_choice_choices: MultipleChoiceDraft[];
  multiple_choice_completion_popup: string;
  multiple_choice_unlock_next_video: boolean;
};

const initialNewCourseTask: NewCourseTaskForm = {
  title: '',
  description: '',
  points: 0,
  task_type: 'upload',
  is_active: true,
  requirementCount: 1,
  video_id: '',
  chapter_id: '',
  order: 0,
  form_items: [createEmptyFormItem()],
  allow_file_upload: false,
  require_all_done: true,
  multiple_choice_intro_popup: '',
  multiple_choice_question: '',
  multiple_choice_choices: [createEmptyChoice(), createEmptyChoice()],
  multiple_choice_completion_popup: '',
  multiple_choice_unlock_next_video: true,
};

const sanitizeFormItems = (items: FormItemDraft[]) =>
  items
    .map((item) => ({
      ...item,
      question: item.question.trim(),
      description: (item.description || '').trim() || undefined,
      placeholder: item.placeholder?.trim() || undefined,
    }))
    .filter((item) => item.question.length > 0);

const buildRequirementsPayload = (data: NewCourseTaskForm) => {
  if (data.task_type === 'form') {
    const cleanedItems = sanitizeFormItems(data.form_items);
    if (cleanedItems.length === 0) {
      throw new Error('Vui lòng thêm ít nhất một câu hỏi cho nhiệm vụ.');
    }
    return {
      form_items: cleanedItems,
      allow_file_upload: data.allow_file_upload,
      require_all_done: data.require_all_done,
    };
  }

  if (data.task_type === 'multiple_choice') {
    const question = (data.multiple_choice_question || '').trim();
    if (!question) {
      throw new Error('Vui lòng nhập câu hỏi cho nhiệm vụ trắc nghiệm.');
    }
    const choices = data.multiple_choice_choices
      .map(c => ({
        id: c.id,
        text: c.text.trim(),
        exp: Math.max(0, parseInt(String(c.exp)) || 0),
        popup_message: (c.popup_message || '').trim() || undefined,
      }))
      .filter(c => c.text.length > 0);
    
    if (choices.length < 2) {
      throw new Error('Vui lòng thêm ít nhất 2 lựa chọn cho câu hỏi trắc nghiệm.');
    }
    
    return {
      intro_popup: (data.multiple_choice_intro_popup || '').trim() || undefined,
      question,
      choices,
      completion_popup: (data.multiple_choice_completion_popup || '').trim() || undefined,
      unlock_next_video: data.multiple_choice_unlock_next_video,
    };
  }

  if (data.task_type === 'custom') {
    return undefined;
  }

  return { count: data.requirementCount || 1 };
};

const extractFormSettings = (task: Task): {
  form_items: FormItemDraft[];
  allow_file_upload: boolean;
  require_all_done: boolean;
} => {
  const requirements = task.requirements || {};
  const items = (requirements.form_items as FormItemDraft[] | undefined) || [];
  return {
    form_items: items.length > 0 ? items : [createEmptyFormItem()],
    allow_file_upload: Boolean(requirements.allow_file_upload),
    require_all_done: requirements.require_all_done !== undefined ? Boolean(requirements.require_all_done) : true,
  };
};

const extractMultipleChoiceSettings = (task: Task): {
  intro_popup: string;
  question: string;
  choices: MultipleChoiceDraft[];
  completion_popup: string;
  unlock_next_video: boolean;
} => {
  const requirements = task.requirements || {};
  const choices = (requirements.choices as any[] | undefined) || [];
  return {
    intro_popup: requirements.intro_popup || '',
    question: requirements.question || '',
    choices: choices.length > 0 
      ? choices.map(c => ({
          id: c.id || createEmptyChoice().id,
          text: c.text || '',
          exp: c.exp || 0,
          popup_message: c.popup_message || '',
          popup_voice_url: c.popup_voice_url || undefined,
        }))
      : [createEmptyChoice(), createEmptyChoice()],
    completion_popup: requirements.completion_popup || '',
    unlock_next_video: requirements.unlock_next_video !== undefined ? Boolean(requirements.unlock_next_video) : true,
  };
};

type FormBuilderProps = {
  data: NewCourseTaskForm;
  setData: React.Dispatch<React.SetStateAction<NewCourseTaskForm>>;
  disabled?: boolean;
  // Voice files state (only for multiple_choice)
  choiceVoiceFiles?: Record<string, File | null>;
  onChoiceVoiceChange?: (choiceId: string, file: File | null) => void;
  introVoiceFile?: File | null;
  onIntroVoiceChange?: (file: File | null) => void;
  completionVoiceFile?: File | null;
  onCompletionVoiceChange?: (file: File | null) => void;
};

const TaskFormBuilderSection: React.FC<FormBuilderProps> = ({ data, setData, disabled }) => {
  const handleItemChange = (itemId: string, field: keyof FormItemDraft, value: any) => {
    setData((prev) => ({
      ...prev,
      form_items: prev.form_items.map((item) =>
        item.id === itemId ? { ...item, [field]: value } : item
      ),
    }));
  };

  const handleAddItem = () => {
    setData((prev) => ({
      ...prev,
      form_items: [...prev.form_items, createEmptyFormItem()],
    }));
  };

  const handleRemoveItem = (itemId: string) => {
    setData((prev) => {
      if (prev.form_items.length <= 1) {
        return prev;
      }
      return {
        ...prev,
        form_items: prev.form_items.filter((item) => item.id !== itemId),
      };
    });
  };

  return (
    <div className="md:col-span-2 space-y-4 border border-red-100 rounded-2xl p-4 bg-red-50/50">
      <div className="flex flex-wrap gap-4 items-center">
        <label className="flex items-center gap-2 text-sm font-semibold text-red-700">
          <input
            type="checkbox"
            className="rounded border-red-400 text-red-600 focus:ring-red-500"
            checked={data.allow_file_upload}
            onChange={(e) =>
              setData((prev) => ({ ...prev, allow_file_upload: e.target.checked }))
            }
            disabled={disabled}
          />
          Cho phép nộp file kèm
        </label>
        <label className="flex items-center gap-2 text-sm font-semibold text-red-700">
          <input
            type="checkbox"
            className="rounded border-red-400 text-red-600 focus:ring-red-500"
            checked={data.require_all_done}
            onChange={(e) =>
              setData((prev) => ({ ...prev, require_all_done: e.target.checked }))
            }
            disabled={disabled}
          />
          Yêu cầu hoàn thành tất cả mục
        </label>
      </div>
      <div className="space-y-4">
        {data.form_items.map((item, index) => (
          <div
            key={item.id}
            className="bg-white rounded-2xl border border-red-100 p-4 shadow-sm space-y-3"
          >
            <div className="flex items-center justify-between">
              <h4 className="text-sm font-bold text-red-700">Câu hỏi {index + 1}</h4>
              <button
                type="button"
                className="text-xs font-bold text-red-500 hover:text-red-700 disabled:opacity-50"
                onClick={() => handleRemoveItem(item.id)}
                disabled={disabled || data.form_items.length <= 1}
              >
                Xóa
              </button>
            </div>
            <label className="text-sm block">
              <span className="text-gray-700 font-semibold">Nội dung câu hỏi*</span>
              <input
                className="mt-1 w-full border border-gray-300 rounded-lg px-3 py-2"
                value={item.question}
                onChange={(e) => handleItemChange(item.id, 'question', e.target.value)}
                disabled={disabled}
                required
              />
            </label>
            <label className="text-sm block">
              <span className="text-gray-700 font-semibold">Mô tả (tuỳ chọn)</span>
              <textarea
                className="mt-1 w-full border border-gray-300 rounded-lg px-3 py-2"
                rows={2}
                value={item.description || ''}
                onChange={(e) => handleItemChange(item.id, 'description', e.target.value)}
                disabled={disabled}
              />
            </label>
            <label className="text-sm block">
              <span className="text-gray-700 font-semibold">Gợi ý hiển thị (placeholder)</span>
              <input
                className="mt-1 w-full border border-gray-300 rounded-lg px-3 py-2"
                value={item.placeholder || ''}
                onChange={(e) => handleItemChange(item.id, 'placeholder', e.target.value)}
                disabled={disabled}
              />
            </label>
            <div className="flex flex-wrap gap-4">
              <label className="flex items-center gap-2 text-sm font-semibold text-gray-700">
                <input
                  type="checkbox"
                  className="rounded border-red-300 text-red-600 focus:ring-red-500"
                  checked={item.require_answer !== false}
                  onChange={(e) =>
                    handleItemChange(item.id, 'require_answer', e.target.checked)
                  }
                  disabled={disabled}
                />
                Bắt buộc nhập câu trả lời
              </label>
              <label className="flex items-center gap-2 text-sm font-semibold text-gray-700">
                <input
                  type="checkbox"
                  className="rounded border-red-300 text-red-600 focus:ring-red-500"
                  checked={item.require_check !== false}
                  onChange={(e) =>
                    handleItemChange(item.id, 'require_check', e.target.checked)
                  }
                  disabled={disabled}
                />
                Bắt buộc tích hoàn thành
              </label>
            </div>
          </div>
        ))}
      </div>
      <button
        type="button"
        className="px-4 py-2 rounded-xl bg-white border border-red-200 text-red-600 text-sm font-extrabold hover:border-red-400 hover:text-red-700 transition"
        onClick={handleAddItem}
        disabled={disabled}
      >
        + Thêm câu hỏi
      </button>
    </div>
  );
};

const MultipleChoiceBuilderSection: React.FC<FormBuilderProps> = ({ 
  data, 
  setData, 
  disabled,
  choiceVoiceFiles = {},
  onChoiceVoiceChange,
  introVoiceFile = null,
  onIntroVoiceChange,
  completionVoiceFile = null,
  onCompletionVoiceChange,
}) => {
  const handleChoiceChange = (choiceId: string, field: keyof MultipleChoiceDraft, value: any) => {
    setData((prev) => ({
      ...prev,
      multiple_choice_choices: prev.multiple_choice_choices.map((choice) =>
        choice.id === choiceId ? { ...choice, [field]: value } : choice
      ),
    }));
  };

  const handleAddChoice = () => {
    setData((prev) => ({
      ...prev,
      multiple_choice_choices: [...prev.multiple_choice_choices, createEmptyChoice()],
    }));
  };

  const handleRemoveChoice = (choiceId: string) => {
    setData((prev) => {
      if (prev.multiple_choice_choices.length <= 2) {
        return prev;
      }
      return {
        ...prev,
        multiple_choice_choices: prev.multiple_choice_choices.filter((choice) => choice.id !== choiceId),
      };
    });
  };

  return (
    <div className="md:col-span-2 space-y-4 border border-blue-100 rounded-2xl p-4 bg-blue-50/50">
      <label className="text-sm block">
        <span className="text-gray-700 font-semibold">🧿 Lời thoại mở đầu (popup Tào Tháo AI) - Hiển thị trước khi làm nhiệm vụ</span>
        <textarea
          className="mt-1 w-full border border-gray-300 rounded-lg px-3 py-2"
          rows={3}
          value={data.multiple_choice_intro_popup}
          onChange={(e) => setData((prev) => ({ ...prev, multiple_choice_intro_popup: e.target.value }))}
          disabled={disabled}
          placeholder="Ví dụ: Đạo hữu đã xem xong lễ nhập môn, nghĩa là tâm đã động. Nhưng để bước vào con đường tu luyện thật sự… đạo hữu cần giải 1 nhiệm vụ đầu tiên. Hãy chọn đáp án phản ánh quyết tâm của đạo hữu."
        />
      </label>
      <label className="text-sm block">
        <span className="text-gray-700 font-semibold">Voice popup mở đầu (tùy chọn)</span>
        <input
          type="file"
          accept="audio/*"
          className="mt-1 w-full border border-gray-300 rounded-lg px-3 py-2 text-sm"
          onChange={(e) => onIntroVoiceChange?.(e.target.files?.[0] || null)}
          disabled={disabled}
        />
        {introVoiceFile && (
          <p className="mt-1 text-xs text-green-600">✓ Đã chọn: {introVoiceFile.name}</p>
        )}
      </label>
      <label className="text-sm block">
        <span className="text-gray-700 font-semibold">Câu hỏi trắc nghiệm*</span>
        <textarea
          className="mt-1 w-full border border-gray-300 rounded-lg px-3 py-2"
          rows={3}
          value={data.multiple_choice_question}
          onChange={(e) => setData((prev) => ({ ...prev, multiple_choice_question: e.target.value }))}
          disabled={disabled}
          placeholder="Ví dụ: Đạo hữu đã sẵn sàng bước vào con đường tu luyện YouTube AI chưa?"
          required
        />
      </label>

      <div className="space-y-4">
        <h4 className="text-sm font-bold text-blue-700">Các lựa chọn (tối thiểu 2):</h4>
        {data.multiple_choice_choices.map((choice, index) => (
          <div
            key={choice.id}
            className="bg-white rounded-2xl border border-blue-100 p-4 shadow-sm space-y-3"
          >
            <div className="flex items-center justify-between">
              <h4 className="text-sm font-bold text-blue-700">Lựa chọn {String.fromCharCode(65 + index)}</h4>
              <button
                type="button"
                className="text-xs font-bold text-blue-500 hover:text-blue-700 disabled:opacity-50"
                onClick={() => handleRemoveChoice(choice.id)}
                disabled={disabled || data.multiple_choice_choices.length <= 2}
              >
                Xóa
              </button>
            </div>
            <label className="text-sm block">
              <span className="text-gray-700 font-semibold">Nội dung lựa chọn*</span>
              <input
                className="mt-1 w-full border border-gray-300 rounded-lg px-3 py-2"
                value={choice.text}
                onChange={(e) => handleChoiceChange(choice.id, 'text', e.target.value)}
                disabled={disabled}
                placeholder="Ví dụ: Có, ta muốn tu luyện đến tận Chân Tiên."
                required
              />
            </label>
            <label className="text-sm block">
              <span className="text-gray-700 font-semibold">EXP nhận được*</span>
              <input
                type="number"
                min="0"
                className="mt-1 w-full border border-gray-300 rounded-lg px-3 py-2"
                value={choice.exp}
                onChange={(e) => handleChoiceChange(choice.id, 'exp', parseInt(e.target.value) || 0)}
                disabled={disabled}
                required
              />
            </label>
            <label className="text-sm block">
              <span className="text-gray-700 font-semibold">Popup message (hiển thị sau khi chọn)</span>
              <textarea
                className="mt-1 w-full border border-gray-300 rounded-lg px-3 py-2"
                rows={2}
                value={choice.popup_message}
                onChange={(e) => handleChoiceChange(choice.id, 'popup_message', e.target.value)}
                disabled={disabled}
                placeholder="Ví dụ: Khá lắm! Quyết tâm là bước đầu để kết nối với đại đạo."
              />
            </label>
            <label className="text-sm block">
              <span className="text-gray-700 font-semibold">Voice popup (tùy chọn)</span>
              <input
                type="file"
                accept="audio/*"
                className="mt-1 w-full border border-gray-300 rounded-lg px-3 py-2 text-sm"
                onChange={(e) => onChoiceVoiceChange?.(choice.id, e.target.files?.[0] || null)}
                disabled={disabled}
              />
              {choiceVoiceFiles[choice.id] && (
                <p className="mt-1 text-xs text-green-600">✓ Đã chọn: {choiceVoiceFiles[choice.id]?.name}</p>
              )}
              {!choiceVoiceFiles[choice.id] && choice.popup_voice_url && (
                <p className="mt-1 text-xs text-blue-600">✓ Đã có voice: {choice.popup_voice_url}</p>
              )}
            </label>
          </div>
        ))}
      </div>
      <button
        type="button"
        className="px-4 py-2 rounded-xl bg-white border border-blue-200 text-blue-600 text-sm font-extrabold hover:border-blue-400 hover:text-blue-700 transition"
        onClick={handleAddChoice}
        disabled={disabled}
      >
        + Thêm lựa chọn
      </button>

      <label className="text-sm block">
        <span className="text-gray-700 font-semibold">Popup kết thúc nhiệm vụ (hiển thị sau khi hoàn thành)</span>
        <textarea
          className="mt-1 w-full border border-gray-300 rounded-lg px-3 py-2"
          rows={2}
          value={data.multiple_choice_completion_popup}
          onChange={(e) => setData((prev) => ({ ...prev, multiple_choice_completion_popup: e.target.value }))}
          disabled={disabled}
          placeholder="Ví dụ: Đạo hữu đã chính thức nhập môn. Hành trình tu luyện bắt đầu từ đây."
        />
      </label>
      <label className="text-sm block">
        <span className="text-gray-700 font-semibold">Voice popup kết thúc (tùy chọn)</span>
        <input
          type="file"
          accept="audio/*"
          className="mt-1 w-full border border-gray-300 rounded-lg px-3 py-2 text-sm"
          onChange={(e) => onCompletionVoiceChange?.(e.target.files?.[0] || null)}
          disabled={disabled}
        />
        {completionVoiceFile && (
          <p className="mt-1 text-xs text-green-600">✓ Đã chọn: {completionVoiceFile.name}</p>
        )}
      </label>

      <label className="flex items-center gap-2 text-sm font-semibold text-blue-700">
        <input
          type="checkbox"
          className="rounded border-blue-400 text-blue-600 focus:ring-blue-500"
          checked={data.multiple_choice_unlock_next_video}
          onChange={(e) =>
            setData((prev) => ({ ...prev, multiple_choice_unlock_next_video: e.target.checked }))
          }
          disabled={disabled}
        />
        Tự động mở khóa video tiếp theo sau khi hoàn thành
      </label>
    </div>
  );
};

const AdminCourseTasksPage: React.FC = () => {
  const { showToast } = useToast();
  const [tasks, setTasks] = useState<Task[]>([]);
  const [loading, setLoading] = useState(false);
  const [creating, setCreating] = useState(false);
  const [newTask, setNewTask] = useState<NewCourseTaskForm>(initialNewCourseTask);
  const [editingTaskId, setEditingTaskId] = useState<number | null>(null);
  const [editingData, setEditingData] = useState<NewCourseTaskForm>(initialNewCourseTask);
  const [savingEdit, setSavingEdit] = useState(false);
  // Voice files state for multiple choice tasks
  const [newTaskChoiceVoiceFiles, setNewTaskChoiceVoiceFiles] = useState<Record<string, File | null>>({});
  const [newTaskIntroVoiceFile, setNewTaskIntroVoiceFile] = useState<File | null>(null);
  const [newTaskCompletionVoiceFile, setNewTaskCompletionVoiceFile] = useState<File | null>(null);
  const [editingChoiceVoiceFiles, setEditingChoiceVoiceFiles] = useState<Record<string, File | null>>({});
  const [editingIntroVoiceFile, setEditingIntroVoiceFile] = useState<File | null>(null);
  const [editingCompletionVoiceFile, setEditingCompletionVoiceFile] = useState<File | null>(null);
  const [courses, setCourses] = useState<Course[]>([]);
  const [chapters, setChapters] = useState<Chapter[]>([]);
  const [videos, setVideos] = useState<Video[]>([]);
  const [selectedCourseId, setSelectedCourseId] = useState<number | ''>('');
  const [selectedChapterId, setSelectedChapterId] = useState<number | ''>('');

  // Filter states
  const [filterCourseId, setFilterCourseId] = useState<number | ''>('');
  const [filterChapterId, setFilterChapterId] = useState<number | ''>('');
  const [filterVideoId, setFilterVideoId] = useState<number | ''>('');

  const fetchTasks = async () => {
    setLoading(true);
    try {
      const params: any = {};
      if (filterVideoId) params.video_id = filterVideoId;
      if (filterChapterId) params.chapter_id = filterChapterId;
      if (filterCourseId) params.course_id = filterCourseId;
      
      const data = await courseTaskService.listCourseTasksAdmin(params);
      setTasks(data.tasks);
    } catch (err: any) {
      showToast(err?.response?.data?.error || 'Không thể tải danh sách nhiệm vụ video', 'error');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    const loadCourses = async () => {
      try {
        const data = await courseService.getCourses(1, 100, undefined, 'all');
        setCourses(data.courses);
      } catch (error) {
        console.error('Error loading courses:', error);
      }
    };
    loadCourses();
  }, []);

  useEffect(() => {
    fetchTasks();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filterCourseId, filterChapterId, filterVideoId]);

  useEffect(() => {
    const loadChapters = async () => {
      if (!selectedCourseId) {
        setChapters([]);
        setVideos([]);
        setSelectedChapterId('');
        return;
      }
      try {
        const chaptersData = await chapterService.getCourseChapters(Number(selectedCourseId));
        setChapters(chaptersData);
      } catch (error) {
        console.error('Error loading chapters:', error);
        setChapters([]);
      }
    };
    loadChapters();
  }, [selectedCourseId]);

  useEffect(() => {
    const loadVideos = async () => {
      if (!selectedCourseId) {
        setVideos([]);
        return;
      }
      try {
        if (selectedChapterId) {
          const videosData = await videoService.getCourseVideos(Number(selectedCourseId));
          const filteredVideos = videosData.filter(v => v.chapter_id === selectedChapterId);
          setVideos(filteredVideos);
        } else {
          const videosData = await videoService.getCourseVideos(Number(selectedCourseId));
          setVideos(videosData);
        }
      } catch (error) {
        console.error('Error loading videos:', error);
        setVideos([]);
      }
    };
    loadVideos();
  }, [selectedCourseId, selectedChapterId]);

  const handleCreateTask = async (e: React.FormEvent) => {
    e.preventDefault();
    // Phải có video_id HOẶC chapter_id
    if (!newTask.video_id && !newTask.chapter_id) {
      showToast('Vui lòng chọn video hoặc chương', 'error');
      return;
    }
    setCreating(true);
    try {
      let requirements = undefined;
      try {
        requirements = buildRequirementsPayload(newTask);
      } catch (error: any) {
        throw new Error(error?.message || 'Vui lòng cấu hình yêu cầu nhiệm vụ hợp lệ');
      }

      const payload: any = {
        title: newTask.title.trim(),
        description: newTask.description.trim() || undefined,
        points: parseInt(String(newTask.points)),
        task_type: newTask.task_type,
        is_active: newTask.is_active,
        order: newTask.order || 0,
      };
      
      // Nếu có video_id, ưu tiên video_id
      if (newTask.video_id) {
        payload.video_id = Number(newTask.video_id);
      } else if (newTask.chapter_id) {
        payload.chapter_id = Number(newTask.chapter_id);
      }
      
      if (requirements) payload.requirements = requirements;

      // Prepare voice files for multiple choice
      const voiceFiles = newTask.task_type === 'multiple_choice' ? {
        choiceVoiceFiles: newTaskChoiceVoiceFiles,
        introVoiceFile: newTaskIntroVoiceFile,
        completionVoiceFile: newTaskCompletionVoiceFile,
      } : undefined;

      const { task } = await courseTaskService.createCourseTask(payload, voiceFiles);
      setTasks((prev) => [task, ...prev]);
      setNewTask(initialNewCourseTask);
      setNewTaskChoiceVoiceFiles({});
      setNewTaskIntroVoiceFile(null);
      setNewTaskCompletionVoiceFile(null);
      setSelectedCourseId('');
      setSelectedChapterId('');
      setVideos([]);
      showToast('Tạo nhiệm vụ khóa học thành công', 'success');
    } catch (err: any) {
      showToast(err?.message || err?.response?.data?.error || 'Không thể tạo nhiệm vụ khóa học', 'error');
    } finally {
      setCreating(false);
    }
  };

  const startEdit = (task: Task) => {
    setEditingTaskId(task.id);
    const reqCount = task.requirements?.count || 1;
    const formSettings =
      task.task_type === 'form'
        ? extractFormSettings(task)
        : {
            form_items: [createEmptyFormItem()],
            allow_file_upload: false,
            require_all_done: true,
          };
    const multipleChoiceSettings =
      task.task_type === 'multiple_choice'
        ? extractMultipleChoiceSettings(task)
        : {
            intro_popup: '',
            question: '',
            choices: [createEmptyChoice(), createEmptyChoice()],
            completion_popup: '',
            unlock_next_video: true,
          };
    setEditingData({
      title: task.title,
      description: task.description || '',
      points: task.points,
      task_type: task.task_type,
      is_active: task.is_active,
      requirementCount: reqCount,
      video_id: task.video_id || '',
      chapter_id: task.chapter_id || '',
      order: task.order || 0,
      form_items: formSettings.form_items,
      allow_file_upload: formSettings.allow_file_upload,
      require_all_done: formSettings.require_all_done,
      multiple_choice_intro_popup: multipleChoiceSettings.intro_popup,
      multiple_choice_question: multipleChoiceSettings.question,
      multiple_choice_choices: multipleChoiceSettings.choices,
      multiple_choice_completion_popup: multipleChoiceSettings.completion_popup,
      multiple_choice_unlock_next_video: multipleChoiceSettings.unlock_next_video,
    });
    setEditingChoiceVoiceFiles({});
    setEditingIntroVoiceFile(null);
    setEditingCompletionVoiceFile(null);
    
    // Load course and chapter from task
    if (task.video_id) {
      const loadVideoData = async () => {
        try {
          const video = await videoService.getVideo(task.video_id!);
          setSelectedCourseId(video.course_id);
          if (video.chapter_id) {
            setSelectedChapterId(video.chapter_id);
          }
        } catch (error) {
          console.error('Error loading video data:', error);
        }
      };
      loadVideoData();
    } else if (task.chapter_id) {
      // Load chapter data
      const loadChapterData = async () => {
        try {
          const chapter = await chapterService.getChapter(task.chapter_id!);
          setSelectedCourseId(chapter.course_id);
          setSelectedChapterId(chapter.id);
        } catch (error) {
          console.error('Error loading chapter data:', error);
        }
      };
      loadChapterData();
    }
  };

  const cancelEdit = () => {
    setEditingTaskId(null);
    setEditingData(initialNewCourseTask);
    setEditingChoiceVoiceFiles({});
    setEditingIntroVoiceFile(null);
    setEditingCompletionVoiceFile(null);
    setSelectedCourseId('');
    setSelectedChapterId('');
  };

  const saveEdit = async (taskId: number) => {
    // Phải có video_id HOẶC chapter_id
    if (!editingData.video_id && !editingData.chapter_id) {
      showToast('Vui lòng chọn video hoặc chương', 'error');
      return;
    }
    setSavingEdit(true);
    try {
      let requirements = undefined;
      try {
        requirements = buildRequirementsPayload(editingData);
      } catch (error: any) {
        throw new Error(error?.message || 'Vui lòng cấu hình yêu cầu nhiệm vụ hợp lệ');
      }

      const payload: any = {
        title: editingData.title.trim(),
        description: editingData.description.trim() || undefined,
        points: parseInt(String(editingData.points)),
        task_type: editingData.task_type,
        is_active: editingData.is_active,
        order: editingData.order || 0,
      };
      
      // Nếu có video_id, ưu tiên video_id
      if (editingData.video_id) {
        payload.video_id = Number(editingData.video_id);
      } else if (editingData.chapter_id) {
        payload.chapter_id = Number(editingData.chapter_id);
      }
      
      if (requirements) payload.requirements = requirements;

      // Prepare voice files for multiple choice
      const voiceFiles = editingData.task_type === 'multiple_choice' ? {
        choiceVoiceFiles: editingChoiceVoiceFiles,
        introVoiceFile: editingIntroVoiceFile,
        completionVoiceFile: editingCompletionVoiceFile,
      } : undefined;

      const { task } = await courseTaskService.updateCourseTask(taskId, payload, voiceFiles);
      setTasks((list) => list.map((it) => (it.id === taskId ? task : it)));
      cancelEdit();
      setEditingChoiceVoiceFiles({});
      setEditingIntroVoiceFile(null);
      setEditingCompletionVoiceFile(null);
      showToast('Cập nhật nhiệm vụ khóa học thành công', 'success');
    } catch (err: any) {
      showToast(err?.message || err?.response?.data?.error || 'Không thể cập nhật nhiệm vụ khóa học', 'error');
    } finally {
      setSavingEdit(false);
    }
  };

  const handleDeleteTask = async (task: Task) => {
    if (!window.confirm(`Xóa nhiệm vụ khóa học "${task.title}"?`)) return;
    try {
      await courseTaskService.deleteCourseTask(task.id);
      setTasks((list) => list.filter((it) => it.id !== task.id));
      if (editingTaskId === task.id) {
        cancelEdit();
      }
    } catch {
      showToast('Không thể xóa nhiệm vụ khóa học', 'error');
    }
  };

  return (
    <>
      <div className="bg-white shadow rounded-xl border border-gray-200 mb-6">
        <div className="p-4 border-b border-gray-200">
          <h2 className="text-lg font-medium">Thêm nhiệm vụ khóa học mới</h2>
          <p className="text-sm text-gray-500 mt-1">
            💡 <strong>Nhiệm vụ khóa học:</strong> Có thể gắn với video cụ thể hoặc với chương. 
            <br />• <strong>Nhiệm vụ video:</strong> User phải xem hết 100% video, làm bài tập và được admin duyệt TẤT CẢ nhiệm vụ của video đó trước khi xem video tiếp theo.
            <br />• <strong>Nhiệm vụ chương:</strong> User phải hoàn thành TẤT CẢ nhiệm vụ của chương trước khi xem video tiếp theo trong chương.
            <br />Nhiệm vụ khóa học có điểm nhưng KHÔNG tính vào level system (chỉ để unlock video).
          </p>
        </div>
        <form className="p-4 grid grid-cols-1 md:grid-cols-2 gap-4" onSubmit={handleCreateTask}>
          <label className="text-sm md:col-span-2">
            <span className="text-gray-700">Tiêu đề*</span>
            <input
              className="mt-1 w-full border border-gray-300 rounded-lg px-3 py-2"
              value={newTask.title}
              onChange={(e) => setNewTask((prev) => ({ ...prev, title: e.target.value }))}
              required
            />
          </label>
          <label className="text-sm md:col-span-2">
            <span className="text-gray-700">Mô tả</span>
            <textarea
              className="mt-1 w-full border border-gray-300 rounded-lg px-3 py-2"
              rows={3}
              value={newTask.description}
              onChange={(e) => setNewTask((prev) => ({ ...prev, description: e.target.value }))}
            />
          </label>
          <label className="text-sm">
            <span className="text-gray-700">Điểm*</span>
            <input
              type="number"
              min="0"
              className="mt-1 w-full border border-gray-300 rounded-lg px-3 py-2"
              value={newTask.points}
              onChange={(e) => setNewTask((prev) => ({ ...prev, points: parseInt(e.target.value) || 0 }))}
              required
            />
          </label>
          <label className="text-sm">
            <span className="text-gray-700">Loại nhiệm vụ*</span>
            <select
              className="mt-1 w-full border border-gray-300 rounded-lg px-3 py-2"
              value={newTask.task_type}
              onChange={(e) => {
                const taskType = e.target.value as Task['task_type'];
                setNewTask((prev) => ({
                  ...prev,
                  task_type: taskType,
                  form_items:
                    taskType === 'form'
                      ? prev.form_items.length > 0
                        ? prev.form_items
                        : [createEmptyFormItem()]
                      : prev.form_items,
                  allow_file_upload: taskType === 'form' ? prev.allow_file_upload : false,
                  require_all_done: taskType === 'form' ? prev.require_all_done : true,
                  multiple_choice_choices:
                    taskType === 'multiple_choice'
                      ? prev.multiple_choice_choices.length >= 2
                        ? prev.multiple_choice_choices
                        : [createEmptyChoice(), createEmptyChoice()]
                      : prev.multiple_choice_choices,
                }));
              }}
            >
              <option value="upload">Upload bài tập</option>
              <option value="custom">Tùy chỉnh</option>
              <option value="form">Form câu hỏi</option>
              <option value="multiple_choice">Trắc nghiệm (Multiple Choice)</option>
            </select>
          </label>
          <label className="text-sm">
            <span className="text-gray-700">Khóa học*</span>
            <select
              className="mt-1 w-full border border-gray-300 rounded-lg px-3 py-2"
              value={selectedCourseId}
              onChange={(e) => {
                setSelectedCourseId(e.target.value ? Number(e.target.value) : '');
                setSelectedChapterId('');
                setNewTask(prev => ({ ...prev, video_id: '' }));
              }}
            >
              <option value="">-- Chọn khóa học --</option>
              {courses.map((course) => (
                <option key={course.id} value={course.id}>
                  {course.title}
                </option>
              ))}
            </select>
          </label>
          {selectedCourseId && (
            <label className="text-sm">
              <span className="text-gray-700">Chương (để lọc video hoặc tạo nhiệm vụ chapter)</span>
              <select
                className="mt-1 w-full border border-gray-300 rounded-lg px-3 py-2"
                value={selectedChapterId}
                onChange={(e) => {
                  setSelectedChapterId(e.target.value ? Number(e.target.value) : '');
                  setNewTask(prev => ({ ...prev, video_id: '', chapter_id: e.target.value ? Number(e.target.value) : '' }));
                }}
              >
                <option value="">-- Tất cả chương --</option>
                {chapters.map((chapter) => (
                  <option key={chapter.id} value={chapter.id}>
                    {chapter.title}
                  </option>
                ))}
              </select>
              <p className="text-xs text-gray-500 mt-1">Chọn chương để lọc video hoặc tạo nhiệm vụ cho chương (không gắn video cụ thể)</p>
            </label>
          )}
          {selectedCourseId && (
            <label className="text-sm md:col-span-2">
              <span className="text-gray-700">Video (tùy chọn - nếu không chọn sẽ tạo nhiệm vụ cho chương)</span>
              <select
                className="mt-1 w-full border border-gray-300 rounded-lg px-3 py-2"
                value={newTask.video_id || ''}
                onChange={(e) => {
                  const videoId = e.target.value ? Number(e.target.value) : '';
                  setNewTask((prev) => ({ 
                    ...prev, 
                    video_id: videoId,
                    chapter_id: videoId ? videos.find(v => v.id === videoId)?.chapter_id || prev.chapter_id : prev.chapter_id
                  }));
                }}
              >
                <option value="">-- Chọn video (hoặc để trống để tạo nhiệm vụ chương) --</option>
                {videos.map((video) => (
                  <option key={video.id} value={video.id}>
                    {video.title} {video.chapter_id ? `(Chương: ${chapters.find(c => c.id === video.chapter_id)?.title || 'N/A'})` : ''}
                  </option>
                ))}
              </select>
              <p className="text-xs text-gray-500 mt-1">
                {newTask.video_id 
                  ? "⚠️ Nhiệm vụ sẽ gắn với video đã chọn. User phải hoàn thành TẤT CẢ nhiệm vụ của video này trước khi xem video tiếp theo."
                  : selectedChapterId 
                    ? "ℹ️ Nhiệm vụ sẽ gắn với chương. User phải hoàn thành TẤT CẢ nhiệm vụ của chương này trước khi xem video tiếp theo trong chương."
                    : "ℹ️ Chọn video để tạo nhiệm vụ video, hoặc chọn chương và để trống video để tạo nhiệm vụ chương."
                }
              </p>
            </label>
          )}
          {newTask.task_type === 'form' && (
            <TaskFormBuilderSection data={newTask} setData={setNewTask} />
          )}
          {newTask.task_type === 'multiple_choice' && (
            <MultipleChoiceBuilderSection 
              data={newTask} 
              setData={setNewTask}
              choiceVoiceFiles={newTaskChoiceVoiceFiles}
              onChoiceVoiceChange={(choiceId, file) => {
                setNewTaskChoiceVoiceFiles((prev) => ({ ...prev, [choiceId]: file }));
              }}
              introVoiceFile={newTaskIntroVoiceFile}
              onIntroVoiceChange={setNewTaskIntroVoiceFile}
              completionVoiceFile={newTaskCompletionVoiceFile}
              onCompletionVoiceChange={setNewTaskCompletionVoiceFile}
            />
          )}
          <label className="text-sm">
            <span className="text-gray-700">Thứ tự</span>
            <input
              type="number"
              min="0"
              className="mt-1 w-full border border-gray-300 rounded-lg px-3 py-2"
              value={newTask.order}
              onChange={(e) => setNewTask((prev) => ({ ...prev, order: parseInt(e.target.value) || 0 }))}
              placeholder="0"
            />
          </label>
          <label className="text-sm">
            <span className="text-gray-700">Trạng thái</span>
            <select
              className="mt-1 w-full border border-gray-300 rounded-lg px-3 py-2"
              value={newTask.is_active ? 'true' : 'false'}
              onChange={(e) => setNewTask((prev) => ({ ...prev, is_active: e.target.value === 'true' }))}
            >
              <option value="true">Kích hoạt</option>
              <option value="false">Vô hiệu hóa</option>
            </select>
          </label>
          <div className="md:col-span-2 flex justify-end">
            <button
              type="submit"
              className="px-4 py-2 bg-primary-600 text-white rounded-lg disabled:opacity-50"
              disabled={creating}
            >
              {creating ? 'Đang tạo...' : 'Tạo nhiệm vụ khóa học'}
            </button>
          </div>
        </form>
      </div>

      <div className="bg-white shadow rounded-xl border border-gray-200">
        <div className="p-4 border-b border-gray-200 flex flex-col md:flex-row gap-3 md:items-center md:justify-between">
          <h2 className="text-lg font-medium">Danh sách nhiệm vụ khóa học</h2>
          <div className="flex gap-2 flex-wrap">
            <select
              className="border border-gray-300 rounded-lg px-3 py-2 text-sm"
              value={filterCourseId}
              onChange={(e) => {
                setFilterCourseId(e.target.value ? Number(e.target.value) : '');
                setFilterChapterId('');
                setFilterVideoId('');
              }}
            >
              <option value="">Tất cả khóa học</option>
              {courses.map((course) => (
                <option key={course.id} value={course.id}>
                  {course.title}
                </option>
              ))}
            </select>
            <button
              onClick={fetchTasks}
              className="px-3 py-2 rounded-lg bg-primary-600 text-white text-sm"
              disabled={loading}
            >
              {loading ? 'Đang tải...' : 'Làm mới'}
            </button>
          </div>
        </div>
        <div className="p-4 overflow-x-auto">
          {loading ? (
            <div className="text-center py-8">Đang tải...</div>
          ) : (
            <table className="min-w-full text-sm">
              <thead>
                <tr className="text-left text-gray-600">
                  <th className="py-2 pr-4">ID</th>
                  <th className="py-2 pr-4">Tiêu đề</th>
                  <th className="py-2 pr-4">Video</th>
                  <th className="py-2 pr-4">Chương</th>
                  <th className="py-2 pr-4">Điểm</th>
                  <th className="py-2 pr-4">Loại</th>
                  <th className="py-2 pr-4">Thứ tự</th>
                  <th className="py-2 pr-4">Trạng thái</th>
                  <th className="py-2 pr-4">Thao tác</th>
                </tr>
              </thead>
              <tbody>
                {tasks.map((task) => {
                  const isEditing = editingTaskId === task.id;
                  const taskVideo = (task as any).video;
                  const taskChapter = (task as any).chapter;
                  const taskCourse = (task as any).course;
                  
                  return (
                    <React.Fragment key={task.id}>
                    <tr className="border-t border-gray-100">
                      <td className="py-2 pr-4">{task.id}</td>
                      <td className="py-2 pr-4">
                        <div>
                          {task.title}
                          {!task.video_id && task.chapter_id && (
                            <span className="ml-2 text-xs bg-blue-100 text-blue-700 px-2 py-0.5 rounded">Chapter Task</span>
                          )}
                          {task.video_id && (
                            <span className="ml-2 text-xs bg-green-100 text-green-700 px-2 py-0.5 rounded">Video Task</span>
                          )}
                        </div>
                        {isEditing ? (
                          <input
                            className="border border-gray-300 rounded-lg px-2 py-1 w-full"
                            value={editingData.title}
                            onChange={(e) => setEditingData((prev) => ({ ...prev, title: e.target.value }))}
                          />
                        ) : (
                          task.title
                        )}
                      </td>
                      <td className="py-2 pr-4">
                        {isEditing ? (
                          <>
                            <select
                              className="border border-gray-300 rounded-lg px-2 py-1 mb-2 w-full"
                              value={selectedCourseId}
                              onChange={(e) => {
                                setSelectedCourseId(e.target.value ? Number(e.target.value) : '');
                                setSelectedChapterId('');
                                setEditingData(prev => ({ ...prev, video_id: '' }));
                              }}
                            >
                              <option value="">Chọn khóa học</option>
                              {courses.map((course) => (
                                <option key={course.id} value={course.id}>
                                  {course.title}
                                </option>
                              ))}
                            </select>
                            {selectedCourseId && (
                              <>
                                <select
                                  className="border border-gray-300 rounded-lg px-2 py-1 mb-2 w-full"
                                  value={selectedChapterId}
                                  onChange={(e) => {
                                    const chapterId = e.target.value ? Number(e.target.value) : '';
                                    setSelectedChapterId(chapterId);
                                    setEditingData(prev => ({ 
                                      ...prev, 
                                      video_id: '',
                                      chapter_id: chapterId
                                    }));
                                  }}
                                >
                                  <option value="">Tất cả chương</option>
                                  {chapters.map((chapter) => (
                                    <option key={chapter.id} value={chapter.id}>
                                      {chapter.title}
                                    </option>
                                  ))}
                                </select>
                                <select
                                  className="border border-gray-300 rounded-lg px-2 py-1 w-full"
                                  value={editingData.video_id || ''}
                                  onChange={(e) => {
                                    const videoId = e.target.value ? Number(e.target.value) : '';
                                    setEditingData((prev) => ({ 
                                      ...prev, 
                                      video_id: videoId,
                                      chapter_id: videoId ? videos.find(v => v.id === videoId)?.chapter_id || prev.chapter_id : prev.chapter_id
                                    }));
                                  }}
                                >
                                  <option value="">Chọn video (hoặc để trống để tạo nhiệm vụ chương)</option>
                                  {videos.map((video) => (
                                    <option key={video.id} value={video.id}>
                                      {video.title}
                                    </option>
                                  ))}
                                </select>
                                {!editingData.video_id && selectedChapterId && (
                                  <p className="text-xs text-gray-500 mt-1">
                                    Nhiệm vụ sẽ gắn với chương đã chọn
                                  </p>
                                )}
                              </>
                            )}
                          </>
                        ) : (
                          <span className="text-gray-600">
                            {task.video_id 
                              ? (taskVideo ? taskVideo.title : `Video #${task.video_id}`)
                              : <span className="text-gray-400 italic">(Nhiệm vụ chương)</span>
                            }
                          </span>
                        )}
                      </td>
                      <td className="py-2 pr-4">
                        <span className="text-gray-600">
                          {taskChapter ? taskChapter.title : (task.chapter_id ? `Chương #${task.chapter_id}` : '-')}
                        </span>
                      </td>
                      <td className="py-2 pr-4">
                        {isEditing ? (
                          <input
                            type="number"
                            min="0"
                            className="border border-gray-300 rounded-lg px-2 py-1 w-full"
                            value={editingData.points}
                            onChange={(e) => setEditingData((prev) => ({ ...prev, points: parseInt(e.target.value) || 0 }))}
                          />
                        ) : (
                          task.points
                        )}
                      </td>
                      <td className="py-2 pr-4">
                        {isEditing ? (
                          <select
                            className="border border-gray-300 rounded-lg px-2 py-1"
                            value={editingData.task_type}
                            onChange={(e) => {
                              const taskType = e.target.value as Task['task_type'];
                              setEditingData((prev) => ({
                                ...prev,
                                task_type: taskType,
                                form_items:
                                  taskType === 'form'
                                    ? prev.form_items.length > 0
                                      ? prev.form_items
                                      : [createEmptyFormItem()]
                                    : prev.form_items,
                                allow_file_upload:
                                  taskType === 'form' ? prev.allow_file_upload : false,
                                require_all_done:
                                  taskType === 'form' ? prev.require_all_done : true,
                                multiple_choice_choices:
                                  taskType === 'multiple_choice'
                                    ? prev.multiple_choice_choices.length >= 2
                                      ? prev.multiple_choice_choices
                                      : [createEmptyChoice(), createEmptyChoice()]
                                    : prev.multiple_choice_choices,
                              }));
                            }}
                          >
                            <option value="upload">Upload bài tập</option>
                            <option value="custom">Tùy chỉnh</option>
                            <option value="form">Form câu hỏi</option>
                            <option value="multiple_choice">Trắc nghiệm (Multiple Choice)</option>
                          </select>
                        ) : (
                          task.task_type
                        )}
                      </td>
                      <td className="py-2 pr-4">
                        {isEditing ? (
                          <input
                            type="number"
                            min="0"
                            className="border border-gray-300 rounded-lg px-2 py-1 w-full"
                            value={editingData.order}
                            onChange={(e) => setEditingData((prev) => ({ ...prev, order: parseInt(e.target.value) || 0 }))}
                          />
                        ) : (
                          task.order || 0
                        )}
                      </td>
                      <td className="py-2 pr-4">
                        {isEditing ? (
                          <select
                            className="border border-gray-300 rounded-lg px-2 py-1"
                            value={editingData.is_active ? 'true' : 'false'}
                            onChange={(e) => setEditingData((prev) => ({ ...prev, is_active: e.target.value === 'true' }))}
                          >
                            <option value="true">Kích hoạt</option>
                            <option value="false">Vô hiệu hóa</option>
                          </select>
                        ) : task.is_active ? (
                          <span className="px-2 py-1 rounded-full bg-green-100 text-green-800 text-xs font-medium">Kích hoạt</span>
                        ) : (
                          <span className="px-2 py-1 rounded-full bg-gray-100 text-gray-800 text-xs font-medium">Vô hiệu hóa</span>
                        )}
                      </td>
                      <td className="py-2 pr-4 space-x-2 whitespace-nowrap">
                        {isEditing ? (
                          <>
                            <button
                              onClick={() => saveEdit(task.id)}
                              className="px-3 py-1 rounded-lg bg-green-600 text-white"
                              disabled={savingEdit}
                            >
                              {savingEdit ? 'Đang lưu...' : 'Lưu'}
                            </button>
                            <button onClick={cancelEdit} className="px-3 py-1 rounded-lg bg-gray-200 text-gray-700">
                              Hủy
                            </button>
                          </>
                        ) : (
                          <>
                            <button
                              onClick={() => startEdit(task)}
                              className="px-3 py-1 rounded-lg bg-blue-600 text-white"
                            >
                              Sửa
                            </button>
                            <button
                              onClick={() => handleDeleteTask(task)}
                              className="px-3 py-1 rounded-lg bg-red-600 text-white"
                            >
                              Xóa
                            </button>
                          </>
                        )}
                      </td>
                    </tr>
                    {isEditing && editingData.task_type === 'form' && (
                      <tr className="border-b border-gray-100 bg-red-50/40">
                        <td colSpan={9} className="py-4">
                          <TaskFormBuilderSection data={editingData} setData={setEditingData} />
                        </td>
                      </tr>
                    )}
                    {isEditing && editingData.task_type === 'multiple_choice' && (
                      <tr className="border-b border-gray-100 bg-blue-50/40">
                        <td colSpan={9} className="py-4">
                          <MultipleChoiceBuilderSection 
                            data={editingData} 
                            setData={setEditingData}
                            choiceVoiceFiles={editingChoiceVoiceFiles}
                            onChoiceVoiceChange={(choiceId, file) => {
                              setEditingChoiceVoiceFiles((prev) => ({ ...prev, [choiceId]: file }));
                            }}
                            introVoiceFile={editingIntroVoiceFile}
                            onIntroVoiceChange={setEditingIntroVoiceFile}
                            completionVoiceFile={editingCompletionVoiceFile}
                            onCompletionVoiceChange={setEditingCompletionVoiceFile}
                          />
                        </td>
                      </tr>
                    )}
                    </React.Fragment>
                  );
                })}
              </tbody>
            </table>
          )}
        </div>
      </div>
    </>
  );
};

export default AdminCourseTasksPage;

